import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import Command from '../../state/actions/command';
import SaveIcon from '@mui/icons-material/Save';
import {theme} from '../../mui/theme';
import StammdatenTab from './StammdatenTab';
import DetailsTab from './DetailsTab';
import BilderTab from './BilderTab';
import Api from '../../api';
import {CompanyHospitality, GalleryImage, StoredImage} from '../../state/types/Company';

const styles = {
    wrapperBox: {
        boxSizing: 'border-box',
        height: '100%',
        overflowY: 'auto',
    },
    formWrapper: {
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
        paddingTop: 3,
    },
    inputFieldSpacing: {
        marginTop: 2.5,
    },
    saveButton: {
        marginTop: 2,
    },
    testCategories: {
        flex: 2,
        [theme.breakpoints.up('sm')]: {
            marginLeft: 2,
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            marginTop: 2,
        },
    },
};

const QUALITY_TRAVEL_REGION_DATA: string[] = [
    'Prignitz',
    'Ruppiner Seenland',
    'Dahme-Seenland',
    'Fläming',
    'Cottbus/Spreewald',
];

const TRAVEL_REGION_DATA: string[] = [
    'Prignitz',
    'Ruppiner Seenland',
    'Uckermark',
    'Barnimer Land',
    'Havelland',
    'Potsdam',
    'Seenland Oder-Spree',
    'Dahme-Seenland',
    'Fläming',
    'Cottbus/Spreewald',
    'Elbe-Elster-Land',
    'Lausitzer Seenland',
];

const BaseData = () => {
    const dispatch = useDispatch();
    const isAdmin = useSelector(Selector.User.isAdmin);
    const isEditor = useSelector(Selector.User.isEditor);
    const hasNormalCompanyAccess = useSelector(Selector.User.hasNormalCompanyAccess);
    // Pure editors (role grants read-all but no company ownership) view Stammdaten read-only.
    const isReadOnly = isEditor && !hasNormalCompanyAccess;
    const activeVersion = useSelector(Selector.QuestionList.activeVersion);
    const selectedCompany = useSelector(Selector.Company.selectedCompany);

    const [activeTab, setActiveTab] = useState(0);
    const [testCategory, setTestCategory] = useState<string|null>(null);

    // Stammdaten state
    const [dataCompanyName, setDataCompanyName] = useState<string>('');
    const [dataRestaurantName, setDataRestaurantName] = useState<string>('');
    const [dataBillingStreet, setDataBillingStreet] = useState<string>('');
    const [dataBillingZip, setDataBillingZip] = useState<string>('');
    const [dataBillingCity, setDataBillingCity] = useState<string>('');
    const [dataContactPerson, setDataContactPerson] = useState<string>('');
    const [dataStreet, setDataStreet] = useState<string>('');
    const [dataZip, setDataZip] = useState<string>('');
    const [dataCity, setDataCity] = useState<string>('');
    const [dataPhone, setDataPhone] = useState<string>('');
    const [dataEmail, setDataEmail] = useState<string>('');
    const [dataWeb, setDataWeb] = useState<string>('');
    const [dataHidden, setDataHidden] = useState<boolean>(false);
    const [dataTravelRegion, setDataTravelRegion] = useState<string>('');

    const [googlePlaceId, setGooglePlaceId] = useState<string>('');
    const [bookingVersion, setBookingVersion] = useState<number>(activeVersion || 5);
    const [bookedMarketing, setBookedMarketing] = useState<boolean>(false);
    const [bookedQualityCheck, setBookedQualityCheck] = useState<boolean>(false);

    const [userBookedQualityCheck, setUserBookedQualityCheck] = useState<boolean>(false);
    const [userBookedMarketing, setUserBookedMarketing] = useState<boolean>(false);
    const [isSubmittingBooking, setIsSubmittingBooking] = useState<boolean>(false);

    // Hospitality state
    const [shortDescription, setShortDescription] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [published, setPublished] = useState<boolean>(false);
    const [hospitalityLogo, setHospitalityLogo] = useState<string | null>(null);
    const [gallery, setGallery] = useState<GalleryImage[]>([]);
    const [hours, setHours] = useState<string>('');

    // Track unsaved logo uploads per company so cleanup doesn't delete them
    const unsavedLogosRef = useRef<Map<string, string>>(new Map());
    const handleSetLogo = useCallback((logo: string | null) => {
        if (selectedCompany) {
            if (logo && logo !== (selectedCompany.hospitality?.logo || null)) {
                unsavedLogosRef.current.set(selectedCompany.id, logo);
            } else {
                unsavedLogosRef.current.delete(selectedCompany.id);
            }
        }
        setHospitalityLogo(logo);
    }, [selectedCompany]);

    useEffect(() => {
        if (!selectedCompany) {
            return;
        }

        setDataCompanyName(selectedCompany.name);
        setDataRestaurantName(selectedCompany.restaurantName);
        setDataBillingStreet(selectedCompany.billingStreet);
        setDataBillingZip(selectedCompany.billingZip);
        setDataBillingCity(selectedCompany.billingCity);
        setDataContactPerson(selectedCompany.contactPerson);
        setDataEmail(selectedCompany.email);
        setDataStreet(selectedCompany.street);
        setDataZip(selectedCompany.zip);
        setDataCity(selectedCompany.city);
        setDataPhone(selectedCompany.phone);
        setDataWeb(selectedCompany.web);
        setDataHidden(selectedCompany.hidden);
        setDataTravelRegion(selectedCompany.travelRegion);

        setTestCategory(selectedCompany.testCategory);

        setUserBookedQualityCheck(false);
        setUserBookedMarketing(false);

        // Hospitality
        const h = selectedCompany.hospitality;
        if (h) {
            setShortDescription(h.shortDescription || '');
            setDescription(h.description || '');
            setPublished(h.published || false);
            setHospitalityLogo(h.logo || null);
            const sortedGallery = [...(h.gallery || [])]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            setGallery(sortedGallery);
            setHours(h.hours || '');
        } else {
            setShortDescription('');
            setDescription('');
            setPublished(false);
            setHospitalityLogo(null);
            setGallery([]);
            setHours('');
        }
        // Cleanup orphan logo files from SeaweedFS
        const savedLogo = h?.logo || null;
        const preserveUrl = unsavedLogosRef.current.get(selectedCompany.id) || null;
        let cancelled = false;
        const cleanupLogos = async () => {
            try {
                const images = await Api.Image.list(selectedCompany.id);
                if (cancelled) return;
                const logos = images.filter(
                    (img: StoredImage) => img.path.includes('/logo/'),
                );
                for (const img of logos) {
                    if (cancelled) return;
                    if (img.url !== savedLogo && img.url !== preserveUrl) {
                        await Api.Image.delete(img.path);
                    }
                }
            } catch {
                // ignore
            }
        };
        // Read-only editors can neither list nor delete images (admin-only API); skip cleanup.
        if (!isReadOnly) {
            cleanupLogos();
        }

        return () => { cancelled = true; };
    }, [selectedCompany, isReadOnly]);

    useEffect(() => {
        if (selectedCompany?.googleRating?.[activeVersion]?.place_id) {
            setGooglePlaceId(selectedCompany?.googleRating?.[activeVersion]?.place_id);
        } else {
            setGooglePlaceId('');
        }
        // Reset bookingVersion to activeVersion when global version changes
        setBookingVersion(activeVersion || 5);
    }, [activeVersion, selectedCompany?.googleRating]);

    // Sync booking checkboxes from bookings[bookingVersion]
    // Fires on: bookingVersion change, company switch, booking data update
    useEffect(() => {
        if (selectedCompany) {
            const booking = selectedCompany.bookings?.[bookingVersion];
            setBookedMarketing(booking?.marketing ?? false);
            setBookedQualityCheck(booking?.qualityCheck ?? false);
        }
    }, [bookingVersion, selectedCompany?.bookings, selectedCompany?.id]);

    const isDescriptionEmpty = !description
        || description === '<p><br></p>'
        || description === '<p></p>'
        || description.replace(/<[^>]*>/g, '').trim().length === 0;

    const canPublish = gallery.length >= 5
        && shortDescription.trim().length > 0
        && !isDescriptionEmpty;

    useEffect(() => {
        if (!canPublish && published) {
            setPublished(false);
        }
    }, [canPublish]);

    if (!selectedCompany) {
        return null;
    }

    const saveBaseData = async () => {
        // Check if booking changed for the selected version
        const currentBooking = selectedCompany.bookings?.[bookingVersion];
        const bookingChanged =
            (bookedQualityCheck !== (currentBooking?.qualityCheck ?? false)) ||
            (bookedMarketing !== (currentBooking?.marketing ?? false));

        const hospitalityData: CompanyHospitality = {
            ...(selectedCompany.hospitality || {}),
            published: canPublish ? published : false,
            logo: hospitalityLogo,
            shortDescription,
            description,
            gallery: gallery.map((g, i) => ({ ...g, order: i })),
            hours: hours || null,
        };

        // Single dispatch — booking is handled sequentially after base data in the saga
        dispatch(Command.Company.updateBasicCompanyData({
            companyId: selectedCompany.id,
            restaurantName: dataRestaurantName,
            companyName: dataCompanyName,
            billingStreet: dataBillingStreet,
            billingZip: dataBillingZip,
            billingCity: dataBillingCity,
            contactPerson: dataContactPerson,
            street: dataStreet,
            zip: dataZip,
            city: dataCity,
            phone: dataPhone,
            email: dataEmail,
            web: dataWeb,
            testCategory: testCategory || undefined,
            hidden: Boolean(dataHidden),
            travelRegion: dataTravelRegion,
            // Keep flat fields unchanged — they are a frozen legacy snapshot
            bookedQualityCheck: selectedCompany.bookedQualityCheck,
            bookedQualityCheckDate: selectedCompany.bookedQualityCheckDate,
            bookedMarketing: selectedCompany.bookedMarketing,
            bookedMarketingDate: selectedCompany.bookedMarketingDate,
            // Versioned booking — saga handles this sequentially after base data save
            versionedBooking: bookingChanged ? {
                version: bookingVersion,
                email: selectedCompany.email,
                bookedQualityCheck: bookedQualityCheck,
                bookedQualityCheckDate: bookedQualityCheck
                    ? (currentBooking?.qualityCheckDate || new Date().toISOString())
                    : '',
                bookedMarketing: bookedMarketing,
                bookedMarketingDate: bookedMarketing
                    ? (currentBooking?.marketingDate || new Date().toISOString())
                    : '',
            } : undefined,
            hospitality: hospitalityData,
        }));
        unsavedLogosRef.current.delete(selectedCompany.id);
    };

    const updateGooglePlaceId = () => {
        if (isAdmin && googlePlaceId !== selectedCompany.googleRating?.[activeVersion]?.place_id) {
            dispatch(Command.Company.addGoogleRating({
                companyId: selectedCompany.id,
                googleRating: {
                    place_id: googlePlaceId,
                },
            }));
        }
    };

    const disableTestcategory = isReadOnly
        || (!(testCategory === undefined || testCategory === null || isAdmin));

    return (
        <Box sx={styles.wrapperBox}>
            <Typography variant={'h1'}>
                Ihre Stammdaten in der Brandenburger Gastlichkeit
            </Typography>

            <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{ marginTop: 2, marginBottom: 1 }}
            >
                <Tab label={'Stammdaten'} />
                <Tab label={'Details'} />
                <Tab label={'Bilder'} />
            </Tabs>

            <Box sx={styles.formWrapper}>
                {/* Tab Content */}
                <Box sx={{ flex: 3 }}>
                    {activeTab === 0 && (
                        <StammdatenTab
                            readOnly={isReadOnly}
                            restaurantName={dataRestaurantName}
                            setRestaurantName={setDataRestaurantName}
                            companyName={dataCompanyName}
                            setCompanyName={setDataCompanyName}
                            billingStreet={dataBillingStreet}
                            setBillingStreet={setDataBillingStreet}
                            billingZip={dataBillingZip}
                            setBillingZip={setDataBillingZip}
                            billingCity={dataBillingCity}
                            setBillingCity={setDataBillingCity}
                            contactPerson={dataContactPerson}
                            setContactPerson={setDataContactPerson}
                            street={dataStreet}
                            setStreet={setDataStreet}
                            zip={dataZip}
                            setZip={setDataZip}
                            city={dataCity}
                            setCity={setDataCity}
                            phone={dataPhone}
                            setPhone={setDataPhone}
                            email={dataEmail}
                            setEmail={setDataEmail}
                            web={dataWeb}
                            setWeb={setDataWeb}
                        />
                    )}

                    {activeTab === 1 && (
                        <DetailsTab
                            readOnly={isReadOnly}
                            companyId={selectedCompany.id}
                            logo={hospitalityLogo}
                            setLogo={handleSetLogo}
                            shortDescription={shortDescription}
                            setShortDescription={setShortDescription}
                            description={description}
                            setDescription={setDescription}
                            published={published}
                            setPublished={setPublished}
                            canPublish={canPublish}
                            galleryCount={gallery.length}
                            hours={hours}
                            setHours={setHours}
                        />
                    )}

                    {activeTab === 2 && (
                        <BilderTab
                            readOnly={isReadOnly}
                            companyId={selectedCompany.id}
                            gallery={gallery}
                            setGallery={setGallery}
                        />
                    )}
                </Box>

                {/* Sidebar - always visible */}
                <Card sx={[styles.testCategories, {display: 'flex', flexDirection: 'column'}]}>
                    <CardContent>
                        <FormControl>
                            <FormLabel component={'legend'}>
                                <Typography variant={'body1'}>
                                    Ihre Testkategorie
                                </Typography>
                            </FormLabel>

                            <RadioGroup value={testCategory} onChange={event => setTestCategory(event.target.value)}>
                                <FormControlLabel
                                    value={'restaurant'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Restaurant'}
                                    labelPlacement={'end'}
                                />
                                <FormControlLabel
                                    value={'excursionRestaurant'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Ausflugslokal'}
                                    labelPlacement={'end'}
                                />
                                <FormControlLabel
                                    value={'cafe'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Café / Eisdiele'}
                                    labelPlacement={'end'}
                                />
                                <FormControlLabel
                                    value={'countryGuesthouse'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Landgasthof'}
                                    labelPlacement={'end'}
                                />
                                <FormControlLabel
                                    value={'pub'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Kneipe / Bar'}
                                    labelPlacement={'end'}
                                />
                                <FormControlLabel
                                    value={'hotel'}
                                    control={<Radio color={'primary'} disabled={disableTestcategory} />}
                                    label={'Hotel garni / Pension'}
                                    labelPlacement={'end'}
                                />
                            </RadioGroup>

                            <FormLabel component={'legend'}>
                                <Typography variant={'body1'}>
                                    Ihre Reiseregion
                                </Typography>
                            </FormLabel>
                            <Select
                                value={dataTravelRegion}
                                disabled={isReadOnly}
                                onChange={(event: SelectChangeEvent) => {
                                    setDataTravelRegion(event.target.value);
                                }}
                            >
                                {TRAVEL_REGION_DATA.map((region: string) => (
                                    <MenuItem value={region} key={region}> {region} </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </CardContent>
                    {isAdmin && (
                        <CardContent sx={{marginTop: 'auto'}}>
                            <TextField
                                label={'Google ID'}
                                variant={'outlined'}
                                fullWidth={true}
                                size={'small'}
                                sx={styles.inputFieldSpacing}
                                value={googlePlaceId}
                                onChange={event => setGooglePlaceId(event.target.value)}
                            />
                            <Button
                                variant={'contained'}
                                style={{marginBottom: 0}}
                                children={'Google Id Updaten'}
                                startIcon={<SaveIcon />}
                                onClick={updateGooglePlaceId}
                                sx={styles.saveButton}
                            />
                        </CardContent>
                    )}
                </Card>
            </Box>

            {/* Booking management card — admin only */}
            {isAdmin && (
                <Card sx={{ marginTop: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant={'body1'} fontWeight={'bold'}>
                                Buchungen
                            </Typography>
                            <FormControl size={'small'} sx={{ minWidth: 160 }}>
                                <InputLabel>Version</InputLabel>
                                <Select
                                    value={bookingVersion}
                                    label={'Version'}
                                    onChange={(e: SelectChangeEvent<number>) =>
                                        setBookingVersion(Number(e.target.value))}
                                >
                                    {Array.from({ length: activeVersion || 5 }, (_, i) => i + 1).map(v => (
                                        <MenuItem key={v} value={v}>
                                            Version {v}{v === activeVersion ? ' (aktuell)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bookedQualityCheck}
                                        onChange={() => setBookedQualityCheck(!bookedQualityCheck)}
                                        color={'primary'}
                                    />
                                }
                                label={'Qualitätscheck'}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={bookedMarketing}
                                        onChange={() => setBookedMarketing(!bookedMarketing)}
                                        color={'primary'}
                                    />
                                }
                                label={'Lieblingslokal'}
                            />
                        </Box>

                        {/* Existing bookings summary */}
                        {selectedCompany.bookings && Object.keys(selectedCompany.bookings).length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant={'caption'} color={'text.secondary'}>
                                    Gebucht:
                                </Typography>
                                {Object.entries(selectedCompany.bookings)
                                    .filter(([, b]) => b.qualityCheck || b.marketing)
                                    .sort(([a], [b]) => Number(a) - Number(b))
                                    .map(([v, b]) => (
                                        <Chip
                                            key={v}
                                            size={'small'}
                                            label={`v${v}: ${[
                                                b.qualityCheck ? 'QC' : '',
                                                b.marketing ? 'BL' : '',
                                            ].filter(Boolean).join(' + ')}`}
                                            color={Number(v) === bookingVersion ? 'primary' : 'default'}
                                            onClick={() => setBookingVersion(Number(v))}
                                        />
                                    ))
                                }
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Booking summary — read-only editors: current version only, locked */}
            {isReadOnly && (() => {
                const currentVersion = activeVersion || 5;
                const currentBooking = selectedCompany.bookings?.[currentVersion];

                return (
                    <Card sx={{ marginTop: 2 }}>
                        <CardContent>
                            <Typography variant={'body1'} fontWeight={'bold'} sx={{ mb: 2 }}>
                                Buchungen (Version {currentVersion})
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={currentBooking?.qualityCheck ?? false}
                                            disabled={true}
                                            color={'primary'}
                                        />
                                    }
                                    label={'Qualitätscheck'}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={currentBooking?.marketing ?? false}
                                            disabled={true}
                                            color={'primary'}
                                        />
                                    }
                                    label={'Lieblingslokal'}
                                />
                            </Box>

                            {/* Existing bookings summary */}
                            {selectedCompany.bookings && Object.keys(selectedCompany.bookings).length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography variant={'caption'} color={'text.secondary'}>
                                        Gebucht:
                                    </Typography>
                                    {Object.entries(selectedCompany.bookings)
                                        .filter(([, b]) => b.qualityCheck || b.marketing)
                                        .sort(([a], [b]) => Number(a) - Number(b))
                                        .map(([v, b]) => (
                                            <Chip
                                                key={v}
                                                size={'small'}
                                                label={`v${v}: ${[
                                                    b.qualityCheck ? 'QC' : '',
                                                    b.marketing ? 'BL' : '',
                                                ].filter(Boolean).join(' + ')}`}
                                                color={Number(v) === currentVersion ? 'primary' : 'default'}
                                            />
                                        ))
                                    }
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {/* Re-registration card — non-admin users (not read-only editors) */}
            {!isAdmin && !isReadOnly && (() => {
                const currentVersion = activeVersion || 5;
                const activeBooking = selectedCompany.bookings?.[currentVersion];
                const hasActiveBooking = activeBooking?.qualityCheck || activeBooking?.marketing;
                const canBookQuality = QUALITY_TRAVEL_REGION_DATA.includes(selectedCompany.travelRegion);

                // Quality package includes marketing (Lieblingslokal)
                const effectiveMarketing = userBookedQualityCheck || userBookedMarketing;

                const submitReRegistration = () => {
                    setIsSubmittingBooking(true);
                    const date = new Date().toISOString();
                    dispatch(Command.Company.bookCompanyForVersion({
                        companyId: selectedCompany.id,
                        version: currentVersion,
                        email: selectedCompany.email,
                        bookedQualityCheck: userBookedQualityCheck,
                        bookedQualityCheckDate: userBookedQualityCheck ? date : '',
                        bookedMarketing: effectiveMarketing,
                        bookedMarketingDate: effectiveMarketing ? date : '',
                        generateContract: true,
                    }));
                };

                return (
                    <Card sx={{ marginTop: 2 }}>
                        <CardContent>
                            <Typography variant={'body1'} fontWeight={'bold'} sx={{ mb: 2 }}>
                                {hasActiveBooking
                                    ? `Sie sind für Version ${currentVersion} angemeldet`
                                    : `Anmeldung für Version ${currentVersion}`
                                }
                            </Typography>

                            {hasActiveBooking ? (
                                <Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                        {activeBooking?.qualityCheck && (
                                            <Chip size={'small'} label={'Qualitätscheck'} color={'success'} />
                                        )}
                                        {activeBooking?.marketing && (
                                            <Chip size={'small'} label={'Lieblingslokal'} color={'success'} />
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Card variant={'outlined'} sx={{ mb: 2 }}>
                                        <CardContent>
                                            <FormControl>
                                                <FormLabel component={'legend'}>
                                                    <Typography variant={'h6'}>
                                                        Qualitätscheck "Brandenburger Gastlichkeit"
                                                    </Typography>
                                                    {!canBookQuality && (
                                                        <Typography variant={'subtitle2'} color={'text.secondary'}>
                                                            (nur in den Regionen Prignitz, Ruppiner Seenland,
                                                            Dahme-Seenland, Fläming und Cottbus/Spreewald verfügbar)
                                                        </Typography>
                                                    )}
                                                    <Typography variant={'body2'}>
                                                        <List sx={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                                                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                                                Detaillierter Online-Qualitätscheck
                                                                mit schriftlicher Auswertung
                                                            </ListItem>
                                                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                                                Plakette zu betrieblichen Werbezwecken
                                                            </ListItem>
                                                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                                                Onlineporträt auf
                                                                www.brandenburger-gastlichkeit.de
                                                            </ListItem>
                                                        </List>
                                                    </Typography>
                                                </FormLabel>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={userBookedQualityCheck}
                                                            disabled={!canBookQuality}
                                                            onChange={event => {
                                                                setUserBookedQualityCheck(event.target.checked);
                                                                if (event.target.checked) {
                                                                    setUserBookedMarketing(false);
                                                                }
                                                            }}
                                                            color={'primary'}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant={'body1'}>
                                                            Hiermit buche ich das Paket
                                                            (Laufzeit 2 Jahre) inkl. Teilnahme
                                                            am Wettbewerb &quot;Brandenburger
                                                            Lieblingslokal&quot;
                                                            <Typography
                                                                variant={'body2'}
                                                                component={'span'}
                                                                display={'block'}
                                                            >
                                                                <span style={{color: 'red'}}>
                                                                    295,00 €
                                                                </span>{' '}
                                                                (zzgl. 19 % MwSt.)
                                                                für DEHOGA Mitglieder
                                                                <br />
                                                                <span style={{color: 'red'}}>
                                                                    355,00 €
                                                                </span>{' '}
                                                                (zzgl. 19 % MwSt.)
                                                                für Nichtmitglieder
                                                            </Typography>
                                                        </Typography>
                                                    }
                                                />
                                            </FormControl>
                                        </CardContent>
                                    </Card>

                                    <Card variant={'outlined'} sx={{ mb: 2 }}>
                                        <CardContent>
                                            <FormControl>
                                                <FormLabel component={'legend'}>
                                                    <Typography variant={'h6'}>
                                                        {userBookedQualityCheck ? '' : 'Nur '}Teilnahme am Wettbewerb
                                                        "Brandenburger Lieblingslokal"
                                                    </Typography>
                                                </FormLabel>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={userBookedQualityCheck || userBookedMarketing}
                                                            disabled={userBookedQualityCheck}
                                                            onChange={event =>
                                                                setUserBookedMarketing(event.target.checked)}
                                                            color={'primary'}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant={'body1'}>
                                                            {userBookedQualityCheck
                                                                ? 'Teilnahme am Wettbewerb'
                                                                    + ' "Brandenburger Lieblingslokal"'
                                                                    + ' (inkl.)'
                                                                : 'Hiermit melde ich mich für den'
                                                                    + ' Wettbewerb "Brandenburger'
                                                                    + ' Lieblingslokal" an'
                                                            }
                                                            {!userBookedQualityCheck && (
                                                                <Typography
                                                                    variant={'body2'}
                                                                    component={'span'}
                                                                    display={'block'}
                                                                >
                                                                    <span style={{color: 'red'}}>
                                                                        60,00 €
                                                                    </span>{' '}
                                                                    (zzgl. 19 % MwSt.)
                                                                    für DEHOGA Mitglieder
                                                                    <br />
                                                                    <span style={{color: 'red'}}>
                                                                        75,00 €
                                                                    </span>{' '}
                                                                    (zzgl. 19 % MwSt.)
                                                                    für Nichtmitglieder
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    }
                                                />
                                            </FormControl>
                                        </CardContent>
                                    </Card>

                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        disabled={
                                            isSubmittingBooking
                                            || (!userBookedQualityCheck && !userBookedMarketing)
                                        }
                                        onClick={submitReRegistration}
                                        sx={{ mt: 1 }}
                                    >
                                        {isSubmittingBooking ? 'Wird gesendet...' : 'Zahlungspflichtig anmelden'}
                                    </Button>
                                </Box>
                            )}

                            {/* Booking history */}
                            {selectedCompany.bookings && Object.keys(selectedCompany.bookings).length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography variant={'caption'} color={'text.secondary'}>
                                        Bisherige Buchungen:
                                    </Typography>
                                    {Object.entries(selectedCompany.bookings)
                                        .filter(([, b]) => b.qualityCheck || b.marketing)
                                        .sort(([a], [b]) => Number(a) - Number(b))
                                        .map(([v, b]) => (
                                            <Chip
                                                key={v}
                                                size={'small'}
                                                label={`v${v}: ${[
                                                    b.qualityCheck ? 'QC' : '',
                                                    b.marketing ? 'BL' : '',
                                                ].filter(Boolean).join(' + ')}`}
                                                color={Number(v) === currentVersion ? 'primary' : 'default'}
                                            />
                                        ))
                                    }
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                );
            })()}

            {!isReadOnly && (
                <Button
                    variant={'contained'}
                    children={'Speichern'}
                    startIcon={<SaveIcon />}
                    onClick={saveBaseData}
                    sx={styles.saveButton}
                />
            )}
        </Box>
    );
};

export default BaseData;
